import { useApolloClient } from "@apollo/client";
import { notification } from "antd";
import { get, isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { FRANCE_ADDRESS_ZIP_CODE_CITY } from "utils/api/graphql/queries/referentials";

const MAX_EMPTY_RESPONSE_RETRIES = 2;
const EMPTY_RESPONSE_RETRY_DELAY = 800;
// The referential only answers for a complete French zip code, so partial input
// must not be sent: it would only produce empty lists and useless retries.
const FRENCH_ZIP_CODE_PATTERN = /^\d{5}$/;

const wait = (duration) =>
  new Promise((resolve) => {
    setTimeout(resolve, duration);
  });

const isCompleteFrenchZipCode = (zipCode) =>
  FRENCH_ZIP_CODE_PATTERN.test(zipCode ? `${zipCode}`.trim() : "");

/**
 * Single source of truth for the France zip-code → cities referential.
 * Use this from Yup validators and one-off loaders; use the hook below in UI.
 */
export const fetchZipCodeCities = async (
  client,
  zipCode,
  { maxRetries = MAX_EMPTY_RESPONSE_RETRIES } = {},
) => {
  const searchedZipCode = zipCode ? `${zipCode}`.trim() : "";
  if (!isCompleteFrenchZipCode(searchedZipCode)) return [];

  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const { data } = await client.query({
        query: FRANCE_ADDRESS_ZIP_CODE_CITY,
        variables: { where: { zipCode: searchedZipCode } },
        // An empty referential response must never be served again from the
        // cache, otherwise the select stays empty until a full page reload.
        fetchPolicy: "network-only",
      });
      const response = get(data, "franceAddressZipCodeCity.response");
      const cities = Array.isArray(response) ? response : [];
      if (!isEmpty(cities) || attempt >= maxRetries) return cities;
    } catch (queryError) {
      lastError = queryError;
      if (attempt >= maxRetries) throw lastError;
    }
    // The referential answers with an empty list when the external geo APIs
    // are unreachable or rate limited, so retry before giving up.
    await wait(EMPTY_RESPONSE_RETRY_DELAY);
  }
  return [];
};

const useZipCodeCities = (zipCode) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();
  const { t } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = t("API", { returnObjects: true });

  const loadCities = useCallback(
    async (code) => fetchZipCodeCities(client, code),
    [client],
  );

  useEffect(() => {
    if (!isCompleteFrenchZipCode(zipCode)) {
      setCities([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    loadCities(zipCode)
      .then((response) => {
        if (!cancelled) setCities(response);
      })
      .catch(() => {
        if (!cancelled) {
          notification.error({ message: API_ERRORS.GENERIC });
          setCities([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // API_ERRORS is rebuilt on every render by i18next.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCities, zipCode]);

  return { cities, loading };
};

export default useZipCodeCities;
